import React, { useState, useEffect } from 'react'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import EmptyTable from '../EmptyTable'
import { useAuth } from "../api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import { HistoryMobileDateRangePicker } from '../HistoryDateRangePickers'
import MobilePaginationActions from './MobilePaginationActions'

// Компонент чипа для статуса тикета
const StatusChip = ({ status }) => {
    const getChipClass = (status) => {
        switch (status) {
            case 'pending':
                return 'ticket-status-chip ticket-status-pending'
            case 'reject':
            case 'rejected':
                return 'ticket-status-chip ticket-status-rejected'
            case 'refund':
            case 'refunded':
            default:
                return 'ticket-status-chip ticket-status-refunded'
        }
    }

    const getStatusText = (status) => {
        switch (status) {
            case 'pending':
                return 'Pending'
            case 'reject':
            case 'rejected':
                return 'Rejected'
            case 'refund':
            case 'refunded':
            default:
                return 'Refunded'
        }
    }

    return (
        <span className={getChipClass(status)} style={{
            border: '1px solid currentColor',
            padding: '4px 8px',
            borderRadius: '7px',
            fontSize: 'var(--text-xs)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'inline-block',
            minWidth: '80px',
            textAlign: 'center',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {getStatusText(status)}
        </span>
    )
}

const MobileTicketsTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [ticketsList, setTicketsList] = useState([])
    const [totalTickets, setTotalTickets] = useState(0)
    const [selectedTicket, setSelectedTicket] = useState(0)
    const [startDate, setStartDate] = useState(() => dayjs())
    const [endDate, setEndDate] = useState(() => dayjs().endOf('month'))
    const [showFilters, setShowFilters] = useState(false)


    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - ticketsList?.length) : 0

    const handleChangePage = (event, newPage) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        setPage(validPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD')
            const dateEndFormated = endDate.format('YYYY-MM-DD')

            const token = await getAccessTokenSilently()

            const ticketsResponse = await props.apiClient.setToken(token).getUserTickets(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setTicketsList(ticketsResponse?.refunds || [])
            setTotalTickets(ticketsResponse?.total || 0)
          } catch (e) {
          }
      }
      callAsync()   
    },[startDate, endDate, page, rowsPerPage, getAccessTokenSilently])

    const handleDateRangeChange = React.useCallback((start, end) => {
      setStartDate(start)
      setEndDate(end)
    }, [])

    const getTicketsDefaultRange = React.useCallback(() => ({
      start: dayjs().startOf('day'),
      end: dayjs().endOf('month')
    }), [])

    const rowClick = (row) => {
      setSelectedTicket(row)
    }

    return (
        <div style={{
            width: '100%',
            height: '100%',
            padding: 'var(--spacing-sm)',
            background: 'var(--black)',
            overflow: 'auto'
        }}>
            
            {/* Мобильные фильтры по дате */}
            <div className="mobile-filters">
                <div 
                    className="mobile-filters-header"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <span className="mobile-filters-title">Date Filters</span>
                    <span style={{ 
                        color: 'var(--text-muted)',
                        transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ▼
                    </span>
                </div>
                
                {showFilters && (
                    <div className="mobile-filters-content">
                        <div className="mobile-filters-grid" style={{ gridTemplateColumns: '1fr' }}>
                            <HistoryMobileDateRangePicker
                                label="Date range"
                                startDate={startDate}
                                endDate={endDate}
                                onRangeChange={handleDateRangeChange}
                                getResetRange={getTicketsDefaultRange}
                            />
                        </div>
                    </div>
                )}
            </div>
            
            {ticketsList?.length > 0 ? (
                <div>
                    {/* Мобильные карточки тикетов */}
                    <div style={{ padding: '0' }}>
                        {ticketsList?.map((row, index) => (
                            <div
                                key={index}
                                onClick={() => rowClick(row)}
                                className="mobile-ticket-card"
                                style={{
                                    borderLeft: selectedTicket?.refundid === row?.refundid ? '4px solid var(--primary-orange)' : '4px solid transparent',
                                }}
                            >
                                {/* Компактная структура карточки */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: 'var(--spacing-sm)'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        {/* Дата и время */}
                                        <div style={{
                                            fontSize: 'var(--text-base)',
                                            fontWeight: '600',
                                            color: 'var(--text-light)',
                                            marginBottom: '4px'
                                        }}>
                                            {moment(row?.createdAt).format('MMM DD, YYYY')}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            color: 'var(--text-muted)',
                                            marginBottom: 'var(--spacing-xs)'
                                        }}>
                                            {moment(row?.createdAt).format('h:mm A')}
                                        </div>
                                        
                                        {/* Proxy ID и Reason в одну строку */}
                                        <div style={{
                                            display: 'flex',
                                            gap: 'var(--spacing-md)',
                                            marginBottom: 'var(--spacing-xs)'
                                        }}>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                                Proxy: <span style={{ color: 'var(--text-light)' }}>{row?.rental?.rentalid || 'N/A'}</span>
                                            </div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                                Reason: <span style={{ color: 'var(--text-light)' }}>{row?.reason || 'N/A'}</span>
                                            </div>
                                        </div>
                                        
                                        {/* Описание */}
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            color: 'var(--text-light)',
                                            lineHeight: '1.4',
                                            marginBottom: row?.status !== 'pending' ? 'var(--spacing-xs)' : '0'
                                        }}>
                                            {row?.message?.replace(/(.{80})..+/, "$1…") || 'No description provided'}
                                        </div>
                                        
                                        {/* Дата закрытия */}
                                        {row?.status !== 'pending' && (
                                            <div style={{
                                                fontSize: 'var(--text-xs)',
                                                color: 'var(--text-muted)'
                                            }}>
                                                Closed: {moment(row?.updatedAt).format('MMM DD, YYYY h:mm A')}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Статус справа */}
                                    <div style={{ marginLeft: 'var(--spacing-sm)' }}>
                                        <StatusChip status={row?.status} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Мобильная пагинация */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-md)',
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        marginTop: 'var(--spacing-md)'
                    }}>
                        {/* Строка с количеством строк на странице */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-sm)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 8px',
                                    fontSize: 'var(--text-sm)',
                                    minWidth: '60px'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Строка с навигацией */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 'var(--spacing-sm)'
                        }}>
                            <MobilePaginationActions 
                                page={page}
                                rowsPerPage={rowsPerPage}
                                count={totalTickets}
                                onPageChange={handleChangePage}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">🎫</div>
                    <h3 className="empty-state-title">No tickets found</h3>
                    <p className="empty-state-description">
                        You don't have any tickets for the selected period.
                    </p>
                </div>
            )}
        </div>
    )
}

export default MobileTicketsTable
